import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { DropProvider } from "./contexts/DropContext.jsx";
import { RADDispatchProvider } from "./contexts/RADDispatchContext.jsx";
import RADHandler from "./RADHandler.jsx";
import { deepCopy } from "@cadenza/rad-domain";
import { HistoryContext, useHistory } from "@cadenza/history-react";

const RADHandlerRoot = ({ rad, setCurrentRAD }) => {
  const {
    state,
    reset,
    update,
    undo,
    redo,
    canUndo,
    canRedo,
    past,
    future,
    undoAll,
    redoAll,
  } = useHistory(rad);

  const previousRadId = useRef();
  const lastVersion = useRef();

  const dispatch = useCallback(
    (action) => {
      if (!action) {
        return;
      }
      switch (action.type) {
        case "REPLACE_RAD":
          update(action.payload, action.source);
          break;
        case "CLEAR_RAD":
          reset({});
          break;
        default:
          console.warn("Invalid action type in reducer:", action?.type);
      }
    },
    [reset, update]
  );

  useEffect(() => {
    const nextRadId = rad?.id || null;

    // Avoid repeatedly resetting history when route has no RAD selected.
    if (!nextRadId) {
      if (previousRadId.current !== null) {
        previousRadId.current = null;
        lastVersion.current = null;
        reset({});
      }
      return;
    }

    if (nextRadId !== previousRadId.current) {
      previousRadId.current = nextRadId;
      lastVersion.current = rad.version;
      reset(rad);
    }
  }, [rad?.id, rad?.version]);

  useEffect(() => {
    if (state?.id && state.version !== lastVersion.current) {
      lastVersion.current = state.version;
      const next = deepCopy(state);
      setCurrentRAD(next);
    }
  }, [state, setCurrentRAD]);

  const historyContextValue = useMemo(
    () => ({
      state,
      reset,
      update,
      undo,
      redo,
      canUndo,
      canRedo,
      past,
      future,
      undoAll,
      redoAll,
      undoCount: past.length,
      redoCount: future.length,
      clearHistory: () => reset(state),
    }),
    [
      state,
      reset,
      update,
      undo,
      redo,
      canUndo,
      canRedo,
      past,
      future,
      undoAll,
      redoAll,
    ]
  );

  return (
    <DropProvider>
      <HistoryContext.Provider value={historyContextValue}>
        <RADDispatchProvider dispatch={dispatch} state={state}>
          <RADHandler />
        </RADDispatchProvider>
      </HistoryContext.Provider>
    </DropProvider>
  );
};

export default RADHandlerRoot;
